import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import {
  dummyConditionViolationStatus,
  dummyConditionViolationTimeseriesData,
} from './data/condition-violations';
import { dummySites, dummyTops, dummyUnits } from './data/ids';
import {
  dummyPenaltyTimelineData,
  dummyPenaltyTimeseriesData,
} from './data/penalty-details';
import {
  dummyPenaltyOverview,
  dummyPenaltyOverviewChartData,
} from './data/penalty-overview';
import { dummyPerformanceWarrantyStates } from './data/performance-warranty';
import { dummyWarrantyIDStatuses } from './data/status';
import { aggregateDummyData } from './utils/aggregate-dummy-data';

// ─────────────────────────────────────────────────────────────────────────────
// Dummy hierarchy levels available in the mock dataset. Extracted so that
// `listLevels()` can be a single-line return statement.
export const DUMMY_LEVELS: IWarrantyRepository.ListLevelsOutput['levels'] = [
  { accureName: 'pack', customerName: 'Rack' },
  { accureName: 'bank', customerName: 'Bank' },
  { accureName: 'system', customerName: 'Site' },
];

export class DummyWarrantyRepository implements IWarrantyRepository {
  key = 'dummyWarrantyRepository';

  // --- overview table ---
  async getAllWarrantyIDStatus(
    input: IWarrantyRepository.GetAllWarrantyIDStatusInput,
  ): Promise<IWarrantyRepository.GetAllWarrantyIDStatusOutput> {
    const filtered = applyFrontendIdFilters(
      dummyWarrantyIDStatuses,
      input.filters,
      input.assetFullFrontendId,
    );

    const level = getFilterLevel(input.filters);
    return aggregateDummyData('warrantyIdStatus', filtered, level);
  }

  // gauges
  async getPerformanceWarrantyStates(
    input: IWarrantyRepository.GetPerformanceWarrantyStatesInput,
  ): Promise<IWarrantyRepository.GetPerformanceWarrantyStatesOutput> {
    // The new implementation returns the metrics map directly
    return dummyPerformanceWarrantyStates(input.assetFullFrontendId);
  }

  // --- penalty overview ---
  async getWarrantyPenaltyChartData(
    input: IWarrantyRepository.GetWarrantyPenaltyChartDataInput,
  ): Promise<IWarrantyRepository.GetWarrantyPenaltyChartDataOutput> {
    const filtered = applyFrontendIdFilters(
      dummyPenaltyOverviewChartData,
      input.filters,
      input.assetFullFrontendId,
    );

    // aggregate
    const level = getFilterLevel(input.filters);
    return aggregateDummyData('penaltyOverviewChart', filtered, level);
  }

  async getPenaltyOverview(
    input: IWarrantyRepository.GetPenaltyOverviewInput,
  ): Promise<IWarrantyRepository.GetPenaltyOverviewOutput> {
    const filtered = applyFrontendIdFilters(
      dummyPenaltyOverview,
      input.filters,
      input.assetFullFrontendId,
    );

    // aggregate
    const level = getFilterLevel(input.filters);
    return aggregateDummyData('penaltyOverviewTable', filtered, level);
  }

  // --- violations section ---
  async getConditionViolationTimeseriesData(
    input: IWarrantyRepository.GetConditionViolationTimeseriesDataInput,
  ): Promise<IWarrantyRepository.GetConditionViolationTimeseriesDataOutput> {
    const filtered = applyFrontendIdFilters(
      dummyConditionViolationTimeseriesData,
      input.filters,
      input.assetFullFrontendId,
    );

    // aggregate
    const level = getFilterLevel(input.filters);
    return aggregateDummyData('conditionViolationTimeseries', filtered, level);
  }

  // condition violations overview table
  async getConditionViolationsStatus(
    input: IWarrantyRepository.GetConditionViolationStatusInput,
  ): Promise<IWarrantyRepository.GetConditionViolationStatusOutput> {
    const filtered = applyFrontendIdFilters(
      dummyConditionViolationStatus,
      input.filters,
      input.assetFullFrontendId,
    );

    // aggregate
    const level = getFilterLevel(input.filters);
    return aggregateDummyData('conditionViolationStatus', filtered, level);
  }

  // --- Penalties details section ---
  // NOTE: Currently not shown!
  async getPenaltyDetails(
    input: IWarrantyRepository.GetPenaltyDetailsInput,
  ): Promise<IWarrantyRepository.GetPenaltyDetailsOutput> {
    return applyFrontendIdFilters(
      dummyPenaltyTimelineData,
      input.filters,
      input.assetFullFrontendId,
    );
  }

  // NOTE: Currently not shown!
  async getPenaltyTimeseriesData(
    input: IWarrantyRepository.PenaltyTimeseriesDataInput,
  ): Promise<IWarrantyRepository.PenaltyTimeseriesDataOutput> {
    return applyFrontendIdFilters(
      dummyPenaltyTimeseriesData,
      input.filters,
      input.assetFullFrontendId,
    );
  }

  // --- helper ---
  async listSites(): Promise<IWarrantyRepository.ListSitesOutput> {
    return dummySites;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  async listLevels(): Promise<IWarrantyRepository.ListLevelsOutput> {
    return { levels: DUMMY_LEVELS };
  }

  async listEntities(
    input: IWarrantyRepository.ListEntitiesInput,
  ): Promise<IWarrantyRepository.ListEntitiesOutput> {
    const { level, filters, assetFullFrontendId } = input;

    type EntityRecord = {
      id: string;
      fullFrontendId: string;
      groupFullId?: string;
      topFullId?: string;
    };

    let source: EntityRecord[] = [];

    switch (level) {
      case 'bank':
        source = dummyTops.tops;
        break;
      case 'pack':
        source = dummyUnits.units;
        break;
      default:
        // No mock data available for the requested level.
        return { entities: [] };
    }

    const filtered = filterChildEntities({
      items: source,
      assetFullFrontendId,
      level,
      filters,
    });

    const entities: IWarrantyRepository.Entity[] = filtered.map((e) => ({
      id: e.id,
      fullFrontendId: e.fullFrontendId,
    }));

    return { entities };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper predicates for ID-based filtering. They live outside the class so the
// callbacks remain allocation-free and easy to unit-test.
function hasExplicitAssetMatch<T extends Record<string, unknown>>(
  item: T,
  assetFullFrontendId: string,
): boolean {
  const idsToCheck = [
    (item as any).fullFrontendId,
    (item as any).unitFullId,
    (item as any).topFullId,
    (item as any).groupFullId,
  ].filter(Boolean) as string[];

  return idsToCheck.includes(assetFullFrontendId);
}

const matchesPackFilter = <T extends Record<string, unknown>>(
  item: T,
  packId: string,
) =>
  (item as any).unitFullId?.includes(packId) ||
  (item as any).fullFrontendId?.includes(packId);

const matchesBankFilter = <T extends Record<string, unknown>>(
  item: T,
  bankId: string,
) => (item as any).topFullId?.includes(bankId);

const matchesSiteFilter = <T extends Record<string, unknown>>(
  item: T,
  siteId: string,
) => (item as any).groupFullId?.includes(siteId);

function applyFrontendIdFilters<
  T extends {
    groupFullId?: string;
    topFullId?: string;
    unitFullId?: string;
    fullFrontendId?: string;
  },
>(
  data: T[],
  filters: IWarrantyRepository.Filters,
  assetFullFrontendId?: string,
): T[] {
  return data
    .filter(
      (item) =>
        !assetFullFrontendId ||
        hasExplicitAssetMatch(item, assetFullFrontendId),
    )
    .filter(
      (item) => !filters.packId || matchesPackFilter(item, filters.packId!),
    )
    .filter(
      (item) => !filters.bankId || matchesBankFilter(item, filters.bankId!),
    )
    .filter(
      (item) => !filters.siteId || matchesSiteFilter(item, filters.siteId!),
    );
}

// Child-entity cascade filtering extracted for readability.
type EntityRecord = {
  id: string;
  fullFrontendId: string;
  groupFullId?: string;
  topFullId?: string;
};

function filterChildEntities({
  items,
  assetFullFrontendId,
  level,
  filters,
}: {
  items: EntityRecord[];
  assetFullFrontendId?: string;
  level: IWarrantyRepository.BatteryLevel;
  filters: IWarrantyRepository.Filters;
}): EntityRecord[] {
  return items.filter((e) => {
    if (assetFullFrontendId && !e.groupFullId?.includes(assetFullFrontendId)) {
      return false;
    }

    if (level === 'pack' && filters.bankId) {
      return e.topFullId === filters.bankId;
    }
    return true;
  });
}

function getFilterLevel(
  filters: IWarrantyRepository.Filters,
): 'site' | 'top' | 'unit' {
  if (filters.packId) return 'unit';
  if (filters.bankId) return 'top';
  return 'site';
}
